/* projectPdf.js — the automatic "Architectural Project Documentation Package".
 *
 * Generated on the STORE side when an order is sent to the Sales team:
 * cover → client info → project specification (all dimensions in mm) →
 * CAD floor plan + elevations (rebuilt from the saved design state) →
 * uploaded drawings/renders → invoices & payment status → summary.
 * The finished PDF is uploaded as an order-chat attachment, so it lands in
 * the workspace Documents tab on both sides. Best-effort by design: a
 * generation failure must never block "Send to Sales Team".
 */
import axios from "axios";
import { jsPDF } from "jspdf";
import { readDesign } from "@/configurator/engine/serialization";
import { buildModuleLibrary } from "@/configurator/engine/catalogBridge";
import { buildPlanDrawingSVG, buildElevationDrawingSVG } from "@/configurator/engine/drawingSvg";
import { stageOf, soMoney, soDate } from "@/store/orders/stages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET = process.env.REACT_APP_BACKEND_URL || "";

/* A4 portrait, points */
const PW = 595.28, PH = 841.89, M = 52;
const INK = [22, 24, 29], MUT = [102, 112, 133], FAINT = [152, 161, 175];
const ACCENT = [124, 92, 250];

/* ── raster helpers ───────────────────────────────────────────────────── */
function svgToPng(svg, pxWidth = 1400) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = (img.height || 1) / (img.width || 1);
        const canvas = document.createElement("canvas");
        canvas.width = pxWidth;
        canvas.height = Math.max(1, Math.round(pxWidth * ratio));
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL("image/png"), ratio });
      } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });
}

/* Fetch an uploaded image (same-backend /uploads) as a data URL + ratio. */
async function fetchImageData(url) {
  const abs = url.startsWith("http") ? url : ASSET + url;
  const resp = await fetch(abs, { headers: { Authorization: `Bearer ${localStorage.getItem("lhc_token") || ""}` } });
  if (!resp.ok) throw new Error("fetch failed");
  const blob = await resp.blob();
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
  const ratio = await new Promise((res) => {
    const img = new Image();
    img.onload = () => res((img.height || 1) / (img.width || 1));
    img.onerror = () => res(0.7);
    img.src = dataUrl;
  });
  const fmt = /png$/i.test(blob.type) ? "PNG" : "JPEG";
  return { dataUrl, ratio, fmt };
}

const isImg = (a) => {
  const t = (a.content_type || "").toLowerCase();
  const n = (a.filename || a.url || "").toLowerCase();
  return t.startsWith("image/") || /\.(png|jpe?g|webp)$/.test(n);
};

/* ── layout helpers ───────────────────────────────────────────────────── */
function makeDoc() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  let pageNo = 1;
  const chrome = (title) => {
    if (pageNo > 1) {
      doc.setFontSize(8.5).setTextColor(...FAINT).setFont("helvetica", "normal");
      doc.text(title, M, 30);
      doc.text(`Page ${pageNo}`, PW - M, 30, { align: "right" });
      doc.setDrawColor(232, 234, 238).setLineWidth(0.6).line(M, 38, PW - M, 38);
    }
    doc.setFontSize(8.5).setTextColor(...FAINT);
    doc.text("LHC Studio — Project Documentation Package", M, PH - 26);
    doc.text(new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }), PW - M, PH - 26, { align: "right" });
  };
  return {
    doc,
    addPage(title) { doc.addPage(); pageNo += 1; chrome(title); return 58; },
    chrome,
  };
}

function sectionTitle(doc, y, text) {
  doc.setFillColor(...ACCENT);
  doc.rect(M, y - 9, 3.5, 13, "F");
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(...INK);
  doc.text(text, M + 12, y + 2);
  return y + 26;
}

function kvRows(doc, y, rows, { colW = (PW - 2 * M) / 2 } = {}) {
  doc.setFontSize(9.5);
  let col = 0, rowY = y;
  for (const [k, v] of rows) {
    const x = M + col * colW;
    doc.setFont("helvetica", "normal").setTextColor(...FAINT);
    doc.text(String(k).toUpperCase(), x, rowY);
    doc.setFont("helvetica", "bold").setTextColor(...INK).setFontSize(11);
    const lines = doc.splitTextToSize(String(v ?? "—"), colW - 18);
    doc.text(lines, x, rowY + 14);
    doc.setFontSize(9.5);
    col += 1;
    if (col === 2) { col = 0; rowY += 16 + lines.length * 13 + 10; }
  }
  return col === 0 ? rowY : rowY + 42;
}

function table(doc, ctx, y, title, head, rows, widths) {
  let yy = y;
  const rowH = 17, x0 = M;
  const drawHead = () => {
    doc.setFillColor(245, 246, 248);
    doc.rect(x0, yy - 11, PW - 2 * M, 16, "F");
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...MUT);
    let x = x0 + 6;
    head.forEach((h, i) => { doc.text(h.toUpperCase(), x, yy); x += widths[i]; });
    yy += rowH;
  };
  drawHead();
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...INK);
  for (const r of rows) {
    if (yy > PH - 70) { yy = ctx.addPage(title); drawHead(); doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...INK); }
    let x = x0 + 6;
    r.forEach((cell, i) => {
      doc.text(String(cell ?? ""), x, yy, { maxWidth: widths[i] - 10 });
      x += widths[i];
    });
    doc.setDrawColor(240, 241, 244).setLineWidth(0.4).line(x0, yy + 5, PW - M, yy + 5);
    yy += rowH;
  }
  return yy + 8;
}

/* Place an image block, breaking to a new page when needed. */
function placeImage(doc, ctx, y, title, img, caption) {
  const w = PW - 2 * M;
  const h = Math.min(w * img.ratio, PH - 150);
  if (y + h > PH - 60) y = ctx.addPage(title);
  doc.setDrawColor(226, 228, 233).setLineWidth(0.8);
  doc.roundedRect(M - 4, y - 4, w + 8, h + 8, 4, 4, "S");
  doc.addImage(img.dataUrl, img.fmt || "PNG", M, y, w, h);
  let yy = y + h + 16;
  if (caption) {
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUT);
    doc.text(caption, M, yy);
    yy += 18;
  }
  return yy;
}

const mm = (v) => (v == null || v === "" ? "—" : `${Math.round(Number(v))} mm`);

/* ── the package ──────────────────────────────────────────────────────── */
export async function buildOrderPackagePdf({ order, client, project, catalogBlob, messages }) {
  const ctx = makeDoc();
  const { doc } = ctx;
  const st = stageOf(order.status);
  const design = (project && project.design) || {};
  const cabinets = design.cabinets || [];
  const room = design.room || {};
  const meta = design.meta || {};
  const snapshot = order.quote_snapshot || {};

  /* ── cover ── */
  doc.setFillColor(11, 12, 16);
  doc.rect(0, 0, PW, PH, "F");
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, PW, 6, "F");
  doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(157, 123, 255);
  doc.text("LHC STUDIO", M, 96);
  doc.setFontSize(30).setTextColor(255, 255, 255);
  doc.text("Project Documentation", M, 168);
  doc.text("Package", M, 204);
  doc.setDrawColor(124, 92, 250).setLineWidth(1.4).line(M, 228, M + 130, 228);
  doc.setFontSize(11).setFont("helvetica", "normal").setTextColor(154, 161, 173);
  const coverRows = [
    ["Order", order.number],
    ["Project", order.project_name || (project && project.name) || "—"],
    ["Client", (client && client.name) || "—"],
    ["Store", order.dealer_id || "—"],
    ["Value", soMoney(order.total)],
    ["Status", st.label],
    ["Date", soDate(new Date().toISOString())],
  ];
  let cy = 300;
  coverRows.forEach(([k, v]) => {
    doc.setTextColor(107, 114, 128).setFontSize(9).setFont("helvetica", "bold");
    doc.text(k.toUpperCase(), M, cy);
    doc.setTextColor(235, 236, 240).setFontSize(13).setFont("helvetica", "normal");
    doc.text(String(v), M + 110, cy);
    cy += 30;
  });
  doc.setFontSize(8.5).setTextColor(107, 114, 128);
  doc.text("Automatically generated when this order was submitted to the Sales team.", M, PH - 40);

  /* ── client information ── */
  let y = ctx.addPage("Client information");
  y = sectionTitle(doc, y, "Client information");
  y = kvRows(doc, y, [
    ["Name", client && client.name],
    ["Company", client && client.company],
    ["Email", client && client.email],
    ["Phone", client && client.phone],
    ["Address", client && [client.address, client.city, client.country].filter(Boolean).join(", ")],
    ["Client since", client && client.created_at ? soDate(client.created_at) : "—"],
  ]);

  /* ── project information (incl. Preview-parity KPIs: volume/weight/wall run) ── */
  let state = null, lib = null;
  try {
    state = readDesign(design);
    lib = buildModuleLibrary(catalogBlob || {});
  } catch (e) { state = null; }
  let volM3 = 0;
  cabinets.forEach((c) => {
    volM3 += ((c.width_mm || 0) / 1000) * ((c.depth_mm || 0) / 1000) * ((c.height_mm || 0) / 1000);
  });
  const wallRunM = state ? (state.walls || []).reduce(
    (s, w) => s + Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]), 0) : 0;

  y += 8;
  y = sectionTitle(doc, y, "Project information");
  y = kvRows(doc, y, [
    ["Project", order.project_name || (project && project.name)],
    ["Order", order.number],
    ["Quote", order.quote_number],
    ["Room width", mm(room.width_mm)],
    ["Room depth", mm(room.depth_mm)],
    ["Room height", mm(room.height_mm)],
    ["Price group", meta.price_group || snapshot.price_group],
    ["Door system", meta.door_system || snapshot.door_system],
    ["Modules", String(cabinets.length || (snapshot.lines || []).length || 0)],
    ["Volume", volM3 > 0 ? `${volM3.toFixed(2)} m³` : "—"],
    ["Weight (est.)", volM3 > 0 ? `~ ${Math.round(volM3 * 160)} kg` : "—"],
    ["Wall run", wallRunM > 0 ? `${wallRunM.toFixed(1)} m (${(state.walls || []).length} walls)` : "—"],
    ["Total value", soMoney(order.total)],
  ]);

  /* module schedule — every dimension in mm */
  if (cabinets.length) {
    y += 4;
    y = sectionTitle(doc, y, "Module schedule");
    y = table(doc, ctx, y, "Module schedule",
      ["#", "SKU", "Type", "Width", "Depth", "Height", "Price"],
      cabinets.map((c, i) => [
        i + 1, c.sku || "—", c.type || "—",
        mm(c.width_mm), mm(c.depth_mm), mm(c.height_mm),
        c.price != null ? soMoney(c.price) : "—",
      ]),
      [24, 118, 100, 62, 62, 62, 66]);
  } else if ((snapshot.lines || []).length) {
    y += 4;
    y = sectionTitle(doc, y, "Quote lines");
    y = table(doc, ctx, y, "Quote lines",
      ["#", "Item", "Amount"],
      snapshot.lines.map((l, i) => [i + 1, l.label || l.code || "—", soMoney(l.total)]),
      [24, 360, 100]);
  }

  /* ── CAD drawings rebuilt from the saved design (state/lib built above) ── */
  if (state && (state.walls || []).length) {
    try {
      const planSvg = buildPlanDrawingSVG(state, lib, { units: "mm" });
      if (planSvg) {
        y = ctx.addPage("Floor plan");
        y = sectionTitle(doc, y, "Floor plan");
        const img = await svgToPng(planSvg, 1500);
        y = placeImage(doc, ctx, y, "Floor plan", img, "Floor plan — dimensions in mm, generated from the saved design state.");
      }
    } catch (e) { /* skip the sheet, keep the package */ }

    for (let i = 0; i < (state.walls || []).length; i++) {
      try {
        const w = state.walls[i];
        const svg = buildElevationDrawingSVG(state, lib, w.id, { units: "mm" });
        if (!svg) continue;
        y = ctx.addPage("Elevations");
        y = sectionTitle(doc, y, `Elevation — Wall W${i + 1}`);
        const img = await svgToPng(svg, 1500);
        y = placeImage(doc, ctx, y, "Elevations", img, `Elevation W${i + 1} — dimensions in mm.`);
      } catch (e) { /* skip this wall */ }
    }
  }

  /* ── uploaded drawings / renders (image attachments from the order thread) ── */
  const atts = (messages || []).flatMap((m) => m.attachments || []);
  const images = atts.filter(isImg);
  if (images.length) {
    y = ctx.addPage("Uploaded drawings & renders");
    y = sectionTitle(doc, y, "Uploaded drawings & renders");
    for (const a of images.slice(0, 12)) {
      try {
        const img = await fetchImageData(a.url);
        y = placeImage(doc, ctx, y, "Uploaded drawings & renders", img, a.filename || "");
        y += 6;
      } catch (e) { /* unreadable upload — skip */ }
    }
  }

  /* ── invoices & payment status ── */
  y = ctx.addPage("Payment & documents");
  y = sectionTitle(doc, y, "Payment status");
  const dep = order.deposit_proofs || [];
  const sec = order.second_payment_proofs || [];
  y = kvRows(doc, y, [
    ["Order value", soMoney(order.total)],
    ["Current stage", st.label],
    ["Deposit proof", dep.length ? `${dep.length} file(s) uploaded` : "Not uploaded yet"],
    ["Balance proof", sec.length ? `${sec.length} file(s) uploaded` : "Not uploaded yet"],
  ]);
  const files = atts.filter((a) => !isImg(a));
  if (files.length || dep.length || sec.length) {
    y += 4;
    y = sectionTitle(doc, y, "Attached documents");
    y = table(doc, ctx, y, "Attached documents",
      ["Document", "Kind"],
      [
        ...dep.map((p) => [p.filename || "deposit proof", "Deposit payment proof"]),
        ...sec.map((p) => [p.filename || "balance proof", "Second payment proof"]),
        ...files.map((a) => [a.filename || "file", "Order attachment"]),
      ],
      [320, 164]);
  }

  /* ── summary ── */
  y = ctx.addPage("Summary");
  y = sectionTitle(doc, y, "Project summary");
  doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(...INK);
  const wallsN = (state && state.walls && state.walls.length) || 0;
  const summary =
    `${order.project_name || "This project"} for ${(client && client.name) || "the client"} was submitted to the LHC Sales team by store ${order.dealer_id || "—"} on ${soDate(new Date().toISOString())}. ` +
    `The configuration contains ${cabinets.length} module(s)${wallsN ? ` across ${wallsN} wall(s)` : ""}` +
    `${room.width_mm ? `, in a room of ${Math.round(room.width_mm)} × ${Math.round(room.depth_mm || 0)} mm (height ${Math.round(room.height_mm || 0)} mm)` : ""}. ` +
    `The order value is ${soMoney(order.total)} (quote ${order.quote_number || "—"}). ` +
    `Current pipeline stage: ${st.label}. This package bundles the client file, full project specification, CAD floor plan and elevations, uploaded drawings and payment documentation for review.`;
  doc.text(doc.splitTextToSize(summary, PW - 2 * M), M, y);

  return doc.output("blob");
}

/* Generate + attach to the order conversation. Returns true on success. */
export async function generateAndAttachPackage({ order, client }) {
  const [project, cat, msgs] = await Promise.all([
    order.project_id ? axios.get(`${API}/projects/${order.project_id}`).then((r) => r.data).catch(() => null) : null,
    axios.get(`${API}/catalog/state`).then((r) => r.data).catch(() => null),
    axios.get(`${API}/chat/order/${order.id}/messages`).then((r) => r.data).catch(() => []),
  ]);
  const blob = await buildOrderPackagePdf({
    order, client, project,
    catalogBlob: (cat && cat.blob) || {},
    messages: msgs || [],
  });
  const fd = new FormData();
  fd.append("file", new File([blob], `${order.number}-package.pdf`, { type: "application/pdf" }));
  const { data: att } = await axios.post(`${API}/chat/order/${order.id}/attachments`, fd,
    { headers: { "Content-Type": "multipart/form-data" } });
  await axios.post(`${API}/chat/order/${order.id}/messages`, {
    body: `📄 Project documentation package generated for ${order.number}.`,
    attachments: [att],
  });
  return true;
}
